import Head from 'next/head';
import { AnimatePresence, motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useState, useEffect, useMemo } from 'react';

// Existing imports
import { getParkFromSlug, getAllParkSlugs } from '@/lib/api';
import {
  capitalize,
  formatDate,
  formatPhoneNumber,
  slugify,
  cleanParkData,
  generateEventStructuredData,
} from '@/lib/util';
import { filterOutClosureEvents } from '@/lib/data';
import {
  getEventBySlug,
  getAllEvents,
  getParkByFullName,
} from '@/lib/db-queries';

// Enhanced components
import HeaderBanner from '@/components/HeaderBanner';
import QuickInfoPanel from '@/components/QuickInfoPanel';
import EventContent from '@/components/EventContent';
import LocationMap from '@/components/LocationMap';
import EventActionButtons from '@/components/EventActionButtons';
import WeatherWidget from '@/components/WeatherWidget';
import ImageGallery from '@/components/ImageGallery';

// Existing components
import ParkSubInfo from '@/components/ParkSubInfo';
import ThingsToDo from '@/components/ThingsToDo';
import Amenities from '@/components/Amenities';
import Articles from '@/components/Articles';
import LoaderSpinner from '@/components/LoaderSpinner';
import LoaderBar from '@/components/LoaderBar';
import useParkInfo from '@/components/useParkInfo';
import useScrollspy from '@/hooks/useScrollspy';
import TableOfContents from '@/components/TableOfContents';
import ParkItem from '@/components/ParkItem';
import useSWR from 'swr';

export default function Event({ event, parkData }) {
  const router = useRouter();
  const slug = router.query;

  const { activeSection } = useScrollspy();
  const [toc, setToc] = useState();
  const [isClient, setIsClient] = useState(false);
  const [showImageGallery, setShowImageGallery] = useState(false);

  const minLevel =
    toc?.reduce((min, item) => (item.level < min ? item.level : min), 10) ?? 0;

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    const headings = document.querySelectorAll('main h1, main h2, main h3');
    const headingArr = [];
    headings.forEach((heading) => {
      const id = heading.id;
      const level = +heading.tagName.replace('H', '');
      const text = heading.textContent + '';
      headingArr.push({ id, level, text });
    });
    setToc(headingArr);
  }, [event, isClient]);

  // Generate structured data once and memoize it
  const structuredData = useMemo(() => {
    return event ? generateEventStructuredData(event) : null;
  }, [event]);

  // Get park information and prepare images - memoized to prevent hydration issues
  // parkData is now passed from getStaticProps (server-side) to avoid Prisma browser errors
  const { park, headerImages } = useMemo(() => {
    if (!event) return { park: null, headerImages: null };

    const parkImages = parkData?.[0]?.images || [];

    // Helper to get absolute URL
    const getAbsoluteUrl = (url) => {
      if (!url) return null;
      if (url.startsWith('/')) {
        return `https://www.nps.gov${url}`;
      }
      return url;
    };

    // Combine event images and park images (same as ImageGallery)
    const eventImgs = event.images || [];
    const allImages = [...eventImgs, ...parkImages];

    // Prepare images for header banner
    let headerImages = null;

    if (allImages.length > 0) {
      // Prefer second image if available (first is often a logo)
      // Reorder: [1, 0, 2, 3, ...] if there are 2+ images
      const reorderedImages =
        allImages.length > 1
          ? [allImages[1], allImages[0], ...allImages.slice(2)]
          : allImages;

      headerImages = reorderedImages
        .map((img) => getAbsoluteUrl(img?.url))
        .filter(Boolean);
    }

    return { park: parkData, headerImages };
  }, [event, parkData]);

  // Safety check for event data
  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-beige-50">
        <div className="text-center">
          <LoaderSpinner />
          <p className="mt-4 text-brown-100 text-sm">
            Loading event details...
          </p>
        </div>
      </div>
    );
  }

  // Extract event data with safe defaults
  const {
    title = 'Event',
    description = '',
    category = '',
    types = [],
    dates = '',
    datestart = '',
    dateend = '',
    times = {},
    timestart = '',
    timeend = '',
    feeinfo = '',
    regresurl = '',
    infoUrl = '',
    contactemailaddress = '',
    contactphonenumber = '',
    contactname = '',
    location = '',
    meetingpoint = '',
    latitude = '',
    longitude = '',
    isregresrequired = '',
    isrecurring = '',
    recurrencedatestart = '',
    recurrencedateend = '',
    parkfullname = '',
    parkCode = '',
    images: eventImages = [],
  } = event;

  // Handle image gallery click
  const handleImageClick = () => {
    if (eventImages && eventImages.length > 1) {
      setShowImageGallery(true);
    }
  };

  return (
    <div className="min-h-screen">
      <Head>
        <title>{`${title || 'Event'} - Explore National Parks Events`}</title>
        <meta
          name="description"
          content={
            description
              ? description.replace(/<[^>]*>/g, '').substring(0, 160)
              : `Join us for ${title} at ${parkfullname || 'a national park'}`
          }
        />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={
            description
              ? description.replace(/<[^>]*>/g, '').substring(0, 160)
              : `Join us for ${title} at ${parkfullname || 'a national park'}`
          }
        />
        <meta property="og:type" content="event" />
        {headerImages && headerImages[0] && (
          <meta property="og:image" content={headerImages[0]} />
        )}

        {/* JSON-LD Event Structured Data */}
        {structuredData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData),
            }}
          />
        )}
      </Head>

      {/* Enhanced Header Banner */}
      <HeaderBanner
        image={headerImages}
        event={event}
        onImageClick={handleImageClick}
      />

      <main className="max-w-8xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Enhanced Hero Section */}
        <div className="relative -mt-10 flex flex-wrap justify-between rounded bg-white max-md:p-8 md:p-10 max-w-8xl px-4 sm:px-6 md:px-8 pb-[55px] mx-auto">
          <div className="flex max-md:flex-col w-full justify-between">
            <div className="w-fit">
              {parkfullname && (
                <motion.a
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3, ease: 'easeOut' }}
                  className="text-brown-100/50 text-sm hover:text-green-100 transition-colors duration-200"
                  href={`/park/${slugify(parkfullname)}`}
                >
                  {parkfullname}
                </motion.a>
              )}
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="text-3xl font-normal leading-tight text-brown-200"
              >
                {title}
              </motion.h1>
            </div>

            {/* NPS.gov Event Link */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              className="text-xs mt-auto max-md:mt-5 min-w-[115px]"
            >
              <a
                href={`https://www.nps.gov/planyourvisit/event-details.htm?id=${event.eventId}`}
                target="_blank"
                rel="noreferrer"
                className="opacity-60 hover:opacity-100 text-brown-100/50 hover:text-green-100 transition-colors duration-200"
              >
                visit on nps.gov{' '}
                <i
                  className="fa-solid fa-up-right-from-square"
                  aria-hidden="true"
                ></i>
              </a>
            </motion.div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="lg:grid lg:grid-cols-[1fr,450px] lg:gap-8 mt-10">
          {/* Left Column - Main Content */}
          <div className="space-y-6">
            {/* Event Content */}
            <EventContent event={event} parkfullname={parkfullname || ''} />

            {/* Image Gallery */}
            <ImageGallery
              images={eventImages || []}
              parkImages={park && park[0]?.images ? park[0].images : []}
              eventTitle={title}
              className="bg-white rounded p-6"
            />

            {/* Location Map */}
            {/* {(latitude || longitude || location || meetingpoint) && (
                            <LocationMap event={event} park={park} />
                        )} */}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Quick Info Panel */}
            <QuickInfoPanel event={event} />

            {/* Action Buttons */}
            {/* <EventActionButtons event={event} /> */}

            {/* Weather Widget */}
            <WeatherWidget event={event} />
          </div>
        </div>

        {/* Table of Contents for Mobile */}
        {isClient && toc && toc.length > 0 && (
          <div className="mt-10 lg:hidden">
            <TableOfContents
              toc={toc}
              activeSection={activeSection}
              minLevel={minLevel}
            />
          </div>
        )}
      </main>
    </div>
  );
}

export async function getStaticProps({ params }) {
  try {
    const slug = params.slug;
    const event = await getEventBySlug(slug);

    // Handle case where event is not found
    if (!event) {
      return {
        notFound: true,
      };
    }

    // Fetch park data server-side to avoid Prisma browser errors
    const parkFullName = event.parkFullName || event.parkfullname;
    let parkData = null;
    if (parkFullName) {
      const park = await getParkByFullName(parkFullName);
      parkData = park ? [cleanParkData(park)] : null;
    }

    // Clean the event data to remove undefined values that can't be serialized
    const cleanedEvent = cleanParkData(event);

    return {
      props: { event: cleanedEvent, parkData },
      revalidate: 86400, // Regenerate every 24 hours (data syncs weekly)
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    // Rethrow: a transient DB failure must not be cached by ISR as a permanent 404
    throw error;
  }
}

export async function getStaticPaths() {
  // Don't pre-render pages at build time - generate on-demand with ISR
  // This reduces build time from ~25 mins to ~2-3 mins
  return {
    paths: [],
    fallback: 'blocking',
  };
}
